import React, {useRef, useEffect, useCallback} from 'react';
import DOMPurify from 'dompurify';
import {BsTypeBold, BsTypeItalic, BsTypeUnderline, BsTypeH2, BsListUl, BsListOl} from 'react-icons/bs';
import './TextEditor.css';

const PURIFY_CONFIG = {
    ALLOWED_TAGS: ['h2', 'p', 'ul', 'ol', 'li', 'strong', 'b', 'br', 'em', 'i', 'u'],
    ALLOWED_ATTR: [],
};

export function sanitizeHtml(html) {
    if (!html) return '';
    return DOMPurify.sanitize(html, PURIFY_CONFIG);
}

const TOOLBAR_BUTTONS = [
    {command: 'formatBlock', arg: 'H2', icon: BsTypeH2, title: 'Rubrik'},
    {command: 'bold', icon: BsTypeBold, title: 'Fetstil'},
    {command: 'italic', icon: BsTypeItalic, title: 'Kursiv'},
    {command: 'underline', icon: BsTypeUnderline, title: 'Understrykning'},
    {command: 'insertUnorderedList', icon: BsListUl, title: 'Punktlista'},
    {command: 'insertOrderedList', icon: BsListOl, title: 'Numrerad lista'},
];

const TextEditor = ({value, onChange, placeholder, rows = 8}) => {
    // Reference to the contentEditable div element
    const editorRef = useRef(null);
    // Flag to track if we caused the change (prevents infinite update loop)
    const isInternalChange = useRef(false);

    // Sync value from parent into the editor div, but only for external changes
    useEffect(() => {
        const el = editorRef.current;
        // Only update if the div exists, the change came from outside, and the value is different
        if (el && !isInternalChange.current && value !== el.innerHTML) {
            el.innerHTML = value || '';
        }
        isInternalChange.current = false;
    }, [value]);

    // Called on every keystroke, sends the editor content to the parent
    const handleInput = useCallback(() => {
        // Mark as internal so the useEffect above won't overwrite the div
        isInternalChange.current = true;
        const html = editorRef.current.innerHTML;
        // Browsers leave a <br> in empty contentEditable divs, treat that as empty
        onChange(html === '<br>' ? '' : html);
    }, [onChange]);

    // Called when user clicks away sanitizes HTML before sending to parent
    const handleBlur = useCallback(() => {
        const raw = editorRef.current.innerHTML;
        const cleaned = sanitizeHtml(raw);
        // If sanitizing changed the HTML, update the div
        if (raw !== cleaned) {
            editorRef.current.innerHTML = cleaned;
        }
        isInternalChange.current = true;
        onChange(cleaned === '<br>' ? '' : cleaned);
    }, [onChange]);

    // Applies formatting to the selected text
    const execCommand = useCallback((cmd, arg) => {
        editorRef.current.focus();

        // Special handling for headings to make them toggle on/off
        if (cmd === 'formatBlock') {
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                // Walk up from the cursor to see if we're already inside the target tag
                let node = selection.anchorNode;
                while (node && node !== editorRef.current) {
                    if (node.nodeType === Node.ELEMENT_NODE && node.tagName === arg) {
                        // Already inside the tag — convert back to normal paragraph
                        document.execCommand('formatBlock', false, 'P');
                        return;
                    }
                    node = node.parentElement;
                }
            }
        }

        document.execCommand(cmd, false, arg || null);
    }, []);

    const minHeight = rows * 24;

    return (
        <div className="TextEditor">
            <div className="TextEditor-toolbar">
                {TOOLBAR_BUTTONS.map(({command, arg, icon: Icon, title}, index) => (
                    <button
                        key={index}
                        type="button"
                        className="TextEditor-toolbar-btn"
                        title={title}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            execCommand(command, arg);
                        }}
                    >
                        <Icon/>
                    </button>
                ))}
            </div>
            <div
                ref={editorRef}
                className="TextEditor-content"
                contentEditable
                data-placeholder={placeholder}
                style={{minHeight: `${minHeight}px`}}
                onInput={handleInput}
                onBlur={handleBlur}
            />
        </div>
    );
};

export default TextEditor;
